import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Briefcase, Lock, ArrowLeft, Check, X } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { TradeAccountRequest, NurseryCustomerTier } from '../../types/nursery';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

function ReviewPanel({
  request,
  tiers,
  onReviewed,
}: {
  request: TradeAccountRequest;
  tiers: NurseryCustomerTier[];
  onReviewed: (r: TradeAccountRequest) => void;
}) {
  const approvalTiers = tiers.filter((t) => t.requires_approval);
  const [grantedTierId, setGrantedTierId] = useState<number | ''>(
    request.requested_tier_id ?? (approvalTiers[0]?.id ?? '')
  );
  const [reviewNotes, setReviewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (action: 'approve' | 'decline') => {
    setError(null);
    setSaving(true);
    try {
      const payload = {
        review_notes: reviewNotes.trim() || undefined,
        ...(action === 'approve' ? { granted_tier_id: grantedTierId === '' ? null : Number(grantedTierId) } : {}),
      };
      const res = action === 'approve'
        ? await nurseryService.approveTradeAccountRequest(request.id, payload)
        : await nurseryService.declineTradeAccountRequest(request.id, payload);
      if (res.success) onReviewed(res.data);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-neutral-100 pt-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[10rem]">
          <label className="block text-xs font-medium text-neutral-500 mb-1">Grant tier</label>
          <select
            value={grantedTierId}
            onChange={(e) => setGrantedTierId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a tier…</option>
            {approvalTiers.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[12rem]">
          <label className="block text-xs font-medium text-neutral-500 mb-1">Notes (optional)</label>
          <input
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <Button variant="primary" size="sm" onClick={() => submit('approve')} disabled={saving || grantedTierId === ''}>
          <Check className="h-4 w-4 mr-1" /> Approve
        </Button>
        <Button variant="outline" size="sm" onClick={() => submit('decline')} disabled={saving}>
          <X className="h-4 w-4 mr-1" /> Decline
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function TradeAccountRequestsList() {
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [status, setStatus] = useState('pending');
  const [requests, setRequests] = useState<TradeAccountRequest[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getTradeAccountRequests(status);
      if (res.success) setRequests(res.data || []);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed to load');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    nurseryService.getCustomerTiers().then((r) => { if (r.success) setTiers(r.data || []); });
  }, []);

  if (licenseLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Reviewing trade-account applications requires an active <span className="font-medium">Nursery Manager</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Trade Account Requests"
        subtitle="Wholesale applications from website customers — approve to unlock trade pricing"
        icon={<Briefcase className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex gap-1 flex-wrap mb-6">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              status === tab.value ? 'bg-primary-600 text-white font-medium' : 'text-neutral-600 hover:bg-neutral-100'
            }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No {status} requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-800 text-sm">{r.business_name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {[r.customer_org_name, r.email, r.phone].filter(Boolean).join(' · ') || 'No contact details'}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-neutral-400">
                      {r.abn && <span>ABN/ACN: <span className="text-neutral-600 font-medium">{r.abn}</span></span>}
                      {r.requested_tier_name && <span>Requested: <span className="text-neutral-600 font-medium">{r.requested_tier_name}</span></span>}
                      <span>Applied {r.created_at?.slice(0, 10)}</span>
                    </div>
                    {r.notes && <p className="text-xs text-neutral-500 mt-1.5 italic">"{r.notes}"</p>}
                  </div>
                  {r.status === 'pending' ? (
                    <Button variant="outline" size="sm" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      Review
                    </Button>
                  ) : (
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${
                    r.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-neutral-100 text-neutral-500'
                  }`}>
                      {r.status === 'approved'
                        ? `Approved${r.granted_tier_name ? ` · ${r.granted_tier_name}` : ''}`
                        : 'Declined'}
                    </span>
                  )}
                </div>

                {r.status === 'pending' && expanded === r.id && (
                  <ReviewPanel
                    request={r}
                    tiers={tiers}
                    onReviewed={(updated) => {
                      setExpanded(null);
                      setRequests((prev) => prev.filter((x) => x.id !== updated.id));
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default TradeAccountRequestsList;
